import ApiClient from '../apiClient';
import { OnlyFansAccount, PaginatedResponse, PaginationParams } from './types';

// ============ Request Params ============

export interface GetOnlyFansAccountsParams extends PaginationParams {
  search?: string;
  status?: 'active' | 'moderation' | 'wait_media' | 'deactivated' | 'all';
  creator_id?: string;
  owner_type?: 'creator' | 'agency';
}

export interface GetUniqueModelsByOfIdParams {
  search?: string;
}

export interface UniqueModelByOfId {
  id: string | number;
  name: string;
  avatar?: string;
  of_user_name?: string;
}

export type UniqueModelsByOfIdResponse = PaginatedResponse<UniqueModelByOfId>;

export interface ModelHeaders {
  accept?: string;
  'accept-encoding'?: string;
  'accept-language'?: string;
  'app-token'?: string;
  cookie?: string;
  priority?: string;
  referer?: string;
  'sec-ch-ua'?: string;
  'sec-ch-ua-mobile'?: string;
  'sec-ch-ua-platform'?: string;
  'sec-fetch-dest'?: string;
  'sec-fetch-mode'?: string;
  'sec-fetch-site'?: string;
  sign?: string;
  time?: string;
  'user-id'?: string;
  'user-agent'?: string;
  'x-bc'?: string;
  'x-of-rev'?: string;
  'x-hash'?: string;
}

// ============ Response Types ============

export type OnlyFansAccountsResponse = PaginatedResponse<OnlyFansAccount>;

// ============ Accounts API ============

export const accountsApi = {
  /**
   * Получить список OnlyFans аккаунтов
   */
  getOnlyFansAccounts: async (params?: GetOnlyFansAccountsParams): Promise<OnlyFansAccountsResponse> => {
    const response = await ApiClient.get('/staff/models', { params });
    return response.data;
  },

  /**
   * Получить детали OnlyFans аккаунта
   */
  getOnlyFansAccount: async (modelId: string): Promise<OnlyFansAccount> => {
    const response = await ApiClient.get(`/staff/models/${modelId}`);
    return response.data;
  },

  /**
   * Обновить OnlyFans аккаунт
   */
  updateOnlyFansAccount: async (
    modelId: string,
    data: Partial<OnlyFansAccount>
  ): Promise<OnlyFansAccount> => {
    const response = await ApiClient.patch(`/staff/models/${modelId}`, data);
    return response.data;
  },

  /**
   * Изменить статус OnlyFans аккаунта
   */
  updateOnlyFansAccountStatus: async (
    modelId: string,
    status: 'active' | 'moderation' | 'wait_media' | 'deactivated'
  ): Promise<OnlyFansAccount> => {
    const response = await ApiClient.patch(`/staff/models/${modelId}`, { status });
    return response.data;
  },

  /**
   * Деактивировать OnlyFans аккаунт
   */
  deactivateOnlyFansAccount: async (modelId: string): Promise<OnlyFansAccount> => {
    return accountsApi.updateOnlyFansAccountStatus(modelId, 'deactivated');
  },

  /**
   * Активировать OnlyFans аккаунт
   */
  activateOnlyFansAccount: async (modelId: string): Promise<OnlyFansAccount> => {
    return accountsApi.updateOnlyFansAccountStatus(modelId, 'active');
  },

  /**
   * Верифицировать OnlyFans аккаунт (изменить статус на active)
   */
  verifyOnlyFansAccount: async (modelId: string): Promise<OnlyFansAccount> => {
    return accountsApi.updateOnlyFansAccountStatus(modelId, 'active');
  },

  /**
   * Получить headers модели OnlyFans
   */
  getModelHeaders: async (modelId: string, ofModelId: string): Promise<string | null> => {
    try {
      const response = await ApiClient.get(`/staff/models/${modelId}/headers`, {
        params: { of_model_id: ofModelId }
      });
      // Бекенд возвращает объект вида { of_user_id, headers, id }
      // Извлекаем только поле headers и конвертируем в JSON строку
      if (response.data && response.data.headers) {
        return JSON.stringify(response.data.headers);
      }
      return null;
    } catch (error: any) {
      // Если хедеры не найдены (404), возвращаем null
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Добавить headers для модели OnlyFans (POST - создание)
   */
  addModelHeaders: async (modelId: string, headers: ModelHeaders): Promise<void> => {
    const response = await ApiClient.post(`/staff/models/${modelId}/headers`, headers);
    return response.data;
  },

  /**
   * Обновить headers модели OnlyFans (PATCH - изменение)
   */
  updateModelHeaders: async (modelId: string, headers: ModelHeaders): Promise<void> => {
    const response = await ApiClient.patch(`/staff/models/${modelId}/headers`, headers);
    return response.data;
  },

  /**
   * Получить уникальные OnlyFans аккаунты по of_id (для фильтров)
   */
  getUniqueModelsByOfId: async (params?: GetUniqueModelsByOfIdParams): Promise<UniqueModelsByOfIdResponse> => {
    const response = await ApiClient.get('/staff/models/unique_by_of_id', { params });
    return response.data;
  },
};

export default accountsApi;
